import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useGetMissedNotifications, notificationKeys } from '../../api/NotificationApi';
import { notificationStorage } from '../utils/notificationStorage';
import { useUserStore } from '../../app/store/useUserStore';
import { isTravellerRole, isSenderRole } from '../utils/roleUtils';
import { useDispatch } from 'react-redux';
import { loadNotifications } from '../../app/store/slices/notificationSlice';
// Minimum time (ms) the tab must be hidden before re-fetching missed notifications on return
const INACTIVITY_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

const MISSED_NOTIF_FETCHED_KEY = 'skyporter_missed_notifications_fetched';

/**
 * Check if missed notifications have already been fetched this session
 */
const hasAlreadyFetchedThisSession = (): boolean => {
  try {
    return sessionStorage.getItem(MISSED_NOTIF_FETCHED_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Mark missed notifications as fetched for this session
 */
const markAsFetchedThisSession = (): void => {
  try {
    sessionStorage.setItem(MISSED_NOTIF_FETCHED_KEY, 'true');
  } catch {
    // sessionStorage unavailable
  }
};

/**
 * Hook to fetch and sync missed notifications when user logs in.
 * Only calls the API once per login session (persisted via sessionStorage).
 * sessionStorage is cleared automatically when the browser tab closes,
 * and is also cleared on logout via logoutCleanup.
 */
export const useMissedNotifications = () => {
  const { user } = useUserStore();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const hasFetchedRef = useRef(hasAlreadyFetchedThisSession());
  // Tracks the timestamp when the tab was hidden
  const hiddenAtRef = useRef<number | null>(null);

  // Convert user role to initiator_type format (traveller -> traveler for API)
  const initiatorType = isTravellerRole(user?.user_role)
    ? 'traveler'
    : isSenderRole(user?.user_role)
    ? 'sender'
    : undefined;

  const { data, isSuccess, isError, error } = useGetMissedNotifications(
    {
      userId: typeof user?.user_id === 'number' ? user.user_id : (user?.negotiateID as number) || 0,
      limit: 30, // Fetch last 30 missed notifications
      initiator_type: initiatorType,
    },
    {
      enabled: !!(user?.user_id || user?.negotiateID) && !!initiatorType && !hasFetchedRef.current,
      staleTime: Infinity, // Only fetch once per session
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  useEffect(() => {
    if (isSuccess && data?.data?.notifications && !hasFetchedRef.current) {
      const missedNotifications = data.data.notifications;

      // Add missed notifications to local storage
      const addedCount = notificationStorage.addMissedNotifications(missedNotifications);

      if (addedCount > 0) {
        // Reload notifications in Redux to reflect the changes
        dispatch(loadNotifications());
      }

      // Mark as fetched — both in ref and sessionStorage
      hasFetchedRef.current = true;
      markAsFetchedThisSession();
    }
  }, [isSuccess, data, dispatch, user?.user_id, user?.negotiateID, user?.user_role, initiatorType]);

  useEffect(() => {
  }, [isError, error]);

  // Re-fetch missed notifications when the tab becomes active after being hidden
  // for longer than INACTIVITY_THRESHOLD_MS (5 minutes)
  useEffect(() => {
    if (!user?.user_id && !user?.negotiateID) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
      } else if (document.visibilityState === 'visible' && hiddenAtRef.current !== null) {
        const hiddenDuration = Date.now() - hiddenAtRef.current;
        hiddenAtRef.current = null;

        if (hiddenDuration >= INACTIVITY_THRESHOLD_MS) {
          // Reset the fetch guard so the query can run again
          hasFetchedRef.current = false;
          // Invalidate the query so React Query re-runs it
          queryClient.invalidateQueries({
            queryKey: notificationKeys.missedNotifications(
              typeof user?.user_id === 'number' ? user.user_id : (user?.negotiateID as number) || 0,
              30,
              isTravellerRole(user?.user_role) ? 'traveler' : isSenderRole(user?.user_role) ? 'sender' : undefined
            ),
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.user_id, user?.negotiateID, user?.user_role, queryClient]);

  return {
    isFetching: !hasFetchedRef.current,
    hasFetched: hasFetchedRef.current,
  };
};

/** Export the key so logoutCleanup can clear it */
export { MISSED_NOTIF_FETCHED_KEY };
